/**
 * Product tile behaviour -- see snippets/product-tile.liquid.
 *
 * Three jobs:
 *
 * 1. The image gallery. Swiping is the browser's own scroll-snap gesture; this
 *    only keeps the dots in step and drives the desktop arrows.
 *
 * 2. Colour swatches. Colours on this store are separate products grouped by
 *    the custom.swatch_group metafield, so picking one swaps the tile in place
 *    -- gallery, title, price, links and availability.
 *    assets/onebone-plp-swatches.js does the same for the legacy card
 *    (snippets/card-product.liquid); it bails on a tile because there is no
 *    .card-product ancestor, so the two never both handle a click.
 *
 * 3. Badges. The tile renders them through the Product labels and badges
 *    section like every other card does; all this has to do is keep them in
 *    step with the colour the tile is showing.
 *
 * Everything is delegated from the document. Facets, pagination and carousels
 * all inject tiles after load, and delegation means those work without being
 * initialised -- nothing here holds a reference to a tile between events.
 *
 * The anchor href on each swatch is left intact, so modified clicks (new tab,
 * middle click) and crawlers still reach the colour's own product page.
 */
if (!window.Eurus.loadedScript.has('product-tile.js')) {
  window.Eurus.loadedScript.add('product-tile.js');

  (function () {
    'use strict';

    var TILE = '[data-product-tile]';
    var TRACK = '[data-product-tile-track]';
    var SWATCH = '.x-card-swatch';

    var pendingFrames = new WeakMap();

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function slidesOf(track) {
      return Array.prototype.slice.call(track.querySelectorAll('.product-tile__slide'));
    }

    function currentIndex(track) {
      var slides = slidesOf(track);
      var closest = 0;
      slides.forEach(function (slide, index) {
        var here = Math.abs(track.scrollLeft - slide.offsetLeft);
        var best = Math.abs(track.scrollLeft - slides[closest].offsetLeft);
        if (here < best) closest = index;
      });
      return closest;
    }

    function paintDots(tile, index) {
      var dots = tile.querySelector('[data-product-tile-dots]');
      if (!dots) return;
      Array.prototype.forEach.call(dots.children, function (dot, dotIndex) {
        dot.classList.toggle('is-current', dotIndex === index);
      });
    }

    /** At either end of the gallery its arrow is disabled, which fades it out. */
    function paintArrows(tile, index, count) {
      var previous = tile.querySelector('[data-product-tile-prev]');
      var next = tile.querySelector('[data-product-tile-next]');

      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= count - 1;
    }

    function paint(tile, track, index) {
      paintDots(tile, index);
      paintArrows(tile, index, slidesOf(track).length);
    }

    function scrollToSlide(tile, step) {
      var track = tile.querySelector(TRACK);
      if (!track) return;

      var slides = slidesOf(track);
      if (!slides.length) return;

      var target = Math.max(0, Math.min(currentIndex(track) + step, slides.length - 1));
      track.scrollTo({
        left: slides[target].offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      paint(tile, track, target);
    }

    /**
     * Replace the gallery with the images of the colour just picked. Falls back
     * to the swatch's featured image when the colour carries no gallery, which
     * is what a card rendered without a gallery limit gives us.
     */
    function rebuildGallery(tile, data) {
      var track = tile.querySelector(TRACK);
      if (!track) return;

      var images = [];
      if (data.swatchGallery) {
        try {
          images = JSON.parse(data.swatchGallery);
        } catch (error) {
          images = [];
        }
      }
      if (!images.length && data.swatchImg) {
        images = [{ src: data.swatchImg, srcset: data.swatchImgSrcset, alt: data.swatchTitle }];
      }
      if (!images.length) return;

      var firstSlide = track.querySelector('.product-tile__slide');
      var href = data.swatchUrl || (firstSlide && firstSlide.getAttribute('href'));

      track.replaceChildren.apply(
        track,
        images.map(function (image, index) {
          var link = document.createElement('a');
          link.className = 'product-tile__slide';
          if (href) link.setAttribute('href', href);
          link.setAttribute('data-product-tile-link', '');
          if (index > 0) {
            link.setAttribute('tabindex', '-1');
            link.setAttribute('aria-hidden', 'true');
          }

          var img = document.createElement('img');
          img.className = 'product-tile__image';
          img.setAttribute('loading', 'lazy');
          img.setAttribute('src', image.src);
          if (image.srcset) img.setAttribute('srcset', image.srcset);
          img.setAttribute('alt', image.alt || data.swatchTitle || '');
          link.appendChild(img);

          return link;
        })
      );

      var dots = tile.querySelector('[data-product-tile-dots]');
      if (dots) {
        dots.replaceChildren.apply(
          dots,
          images.map(function (image, index) {
            var dot = document.createElement('span');
            if (index === 0) dot.classList.add('is-current');
            return dot;
          })
        );
      }

      // A colour with one image has nothing to page through.
      var single = images.length < 2;
      tile.querySelectorAll('[data-product-tile-prev], [data-product-tile-next]').forEach(function (arrow) {
        arrow.hidden = single;
      });
      if (dots) dots.hidden = single;

      track.scrollTo({ left: 0, behavior: 'auto' });
      paintArrows(tile, 0, images.length);
    }

    /*
     * Badges are per colour: NEW is applied to particular products and a sale
     * label reads that product's own compare-at price. Each swatch carries its
     * colour's payload, so a swap is the same two steps the theme's variant
     * picker does on the legacy card (assets/variant-select-card.js) -- replace
     * x-labels-data, then ask the store to render it again. The store clears
     * the badges already on this tile before it re-renders.
     */
    function repaintLabels(tile, labels) {
      if (!labels || !window.Alpine) return;

      var host = tile.querySelector('[data-product-tile-labels]');
      var store = Alpine.store('xBadges');
      if (!host || !store) return;

      host.setAttribute('x-labels-data', labels);
      store.load(host, function () {}, null, true);
    }

    function applySwatch(tile, swatch) {
      var data = swatch.dataset;
      var cta = tile.querySelector('[data-product-tile-cta]');

      rebuildGallery(tile, data);

      // The CTA carries data-product-tile-link too, so picking a colour points
      // the whole tile -- images, title and Choose options -- at that product.
      if (data.swatchUrl) {
        tile.querySelectorAll('[data-product-tile-link]').forEach(function (link) {
          link.setAttribute('href', data.swatchUrl);
        });
      }

      if (data.swatchTitle) {
        tile.querySelectorAll('.x-card-title').forEach(function (title) {
          title.textContent = data.swatchTitle;
        });

        if (cta && tile.dataset.chooseFor) {
          cta.setAttribute('aria-label', tile.dataset.chooseFor.replace('[product]', data.swatchTitle));
        }
      }

      if (data.swatchPriceHtml) {
        tile.querySelectorAll('.x-card-price').forEach(function (price) {
          price.innerHTML = data.swatchPriceHtml;
        });
      }

      if (data.swatchAvailable) {
        var available = data.swatchAvailable === 'true';
        var ctaLabel = tile.querySelector('[data-product-tile-cta-label]');

        // Why a colour is unavailable is per colour too: a drop that has not
        // opened yet reads Coming soon, one that ran out reads Sold out.
        var unavailableLabel = data.swatchComingSoon === 'true'
          ? tile.dataset.comingSoonLabel
          : tile.dataset.soldLabel;

        // The CTA is a link to the product page, so a sold-out colour dims it
        // rather than disabling it -- the page still has the size run and the
        // back-in-stock form.
        if (cta) cta.toggleAttribute('data-unavailable', !available);
        if (ctaLabel) {
          ctaLabel.textContent = available ? tile.dataset.chooseLabel : unavailableLabel;
        }
      }

      repaintLabels(tile, data.swatchLabels);

      tile.querySelectorAll(SWATCH).forEach(function (item) {
        var isCurrent = item.dataset.swatchPid === data.swatchPid;
        item.classList.toggle('is-active', isCurrent);
        if (isCurrent) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      return true;
    }

    document.addEventListener(
      'click',
      function (event) {
        if (!event.target.closest) return;

        var arrow = event.target.closest('[data-product-tile-prev], [data-product-tile-next]');
        if (arrow) {
          var arrowTile = arrow.closest(TILE);
          if (arrowTile) {
            scrollToSlide(arrowTile, arrow.hasAttribute('data-product-tile-prev') ? -1 : 1);
          }
          return;
        }

        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var swatch = event.target.closest(SWATCH);
        if (!swatch) return;

        var tile = swatch.closest(TILE);
        if (!tile) return;

        if (!applySwatch(tile, swatch)) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true // capture: run before the anchor's own navigation
    );

    // Scroll doesn't bubble, so the gallery's own scrolling is caught on the
    // way down instead.
    document.addEventListener(
      'scroll',
      function (event) {
        var track = event.target;
        if (!track.matches || !track.matches(TRACK)) return;

        var tile = track.closest(TILE);
        if (!tile) return;

        cancelAnimationFrame(pendingFrames.get(track));
        pendingFrames.set(
          track,
          requestAnimationFrame(function () {
            paint(tile, track, currentIndex(track));
          })
        );
      },
      true
    );
  })();
}
